import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Play, Clock, Plus, Save, Lightbulb } from 'lucide-react';

import { Chapter } from '../services/geminiService';

interface Note {
  id: string;
  timestamp: string;
  title: string;
  content: string;
  createdAt: string;
}

interface ChapterSummaryProps {
  chapter: Chapter;
  isActive: boolean;
  onChapterClick: (chapterId: string) => void;
  onJumpToTime: (time: number) => void;
  onAddNote: (note: Omit<Note, 'id' | 'createdAt'>) => void;
}

export function ChapterSummary({
  chapter,
  isActive,
  onChapterClick,
  onJumpToTime,
  onAddNote
}: ChapterSummaryProps) {
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const duration = chapter.endTime - chapter.startTime;

  const handleSaveNote = () => {
    if (noteTitle.trim() && noteContent.trim()) {
      onAddNote({
        timestamp: chapter.startTime.toString(),
        title: noteTitle.trim(),
        content: noteContent.trim()
      });
      setNoteTitle('');
      setNoteContent('');
      setShowNoteForm(false);
    }
  };

  return (
    <Card className={`overflow-hidden rounded-[22px] border p-0 shadow-none transition-all duration-200 ${isActive ? 'border-[#6d4aff] bg-[#faf9ff]' : 'border-[#e8e4dc] bg-white hover:border-[#cfc7ff]'
      }`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-6 pb-5 pt-6">
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex items-center gap-2">
            <Badge variant="secondary" className="rounded-full bg-[#f0edff] px-2.5 py-1 text-[11px] font-bold text-[#5b3ee4]">
              <Clock className="w-3 h-3 mr-1" />
              {formatTime(duration)}
            </Badge>
            <Badge variant="outline" className="rounded-full border-[#e8e4dc] px-2.5 py-1 text-[11px] text-slate-500">
              {formatTime(chapter.startTime)} - {formatTime(chapter.endTime)}
            </Badge>
          </div>
          <h4 className="text-lg font-extrabold leading-snug tracking-[-.02em] text-[#17141f]">{chapter.title}</h4>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            onChapterClick(chapter.id);
            onJumpToTime(chapter.startTime);
          }}
          aria-label={`${chapter.title} 재생`}
          className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-[#17141f] p-0 text-white hover:bg-[#6d4aff] hover:text-white"
        >
          <Play className="w-4 h-4" />
        </Button>
      </div>

      {/* Step-by-Step Guide (Main Content) */}
      <div className="space-y-5 border-t border-[#eeeae3] px-6 py-6">
        <div>
          <p className="mb-3 text-[11px] font-black tracking-[.16em] text-[#6d4aff]">CHAPTER NOTE</p>
          <div className="whitespace-pre-line text-[15px] font-medium leading-7 text-slate-700">
            {chapter.summary}
          </div>
        </div>

        {/* Pro Tips (Sub Content) */}
        {chapter.keyPoints.length > 0 ? <div className="rounded-2xl bg-[#f5f3ed] p-5">
          <h5 className="mb-3 flex items-center gap-2 text-sm font-bold text-[#383241]">
            <Lightbulb className="h-4 w-4 text-[#d08b18]" /> 기억해야 할 핵심
          </h5>
          <div className="grid gap-2">
            {chapter.keyPoints.map((point, index) => (
              <div key={index} className="flex items-start gap-2.5 text-sm text-slate-600">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#6d4aff]" />
                <span className="leading-relaxed">{point}</span>
              </div>
            ))}
          </div>
        </div> : null}
      </div>

      {/* Note Form */}
      {showNoteForm && (
        <div className="mt-4 pt-4 border-t space-y-3">
          <h5 className="text-sm font-medium">노트 추가</h5>
          <Input
            placeholder="노트 제목을 입력하세요"
            value={noteTitle}
            onChange={(e) => setNoteTitle(e.target.value)}
          />
          <Textarea
            placeholder="이 챕터에서 배운 내용이나 중요한 포인트를 기록하세요..."
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            rows={3}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSaveNote}>
              <Save className="w-4 h-4 mr-1" />
              저장
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowNoteForm(false);
                setNoteTitle('');
                setNoteContent('');
              }}
            >
              취소
            </Button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 border-t border-[#eeeae3] px-6 py-4">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 rounded-xl border-[#ded8cf]"
          onClick={() => {
            onChapterClick(chapter.id);
            onJumpToTime(chapter.startTime);
          }}
        >
          <Play className="w-4 h-4 mr-1" />
          이 구간 재생
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="flex-1 rounded-xl text-slate-600"
          onClick={() => setShowNoteForm(!showNoteForm)}
        >
          <Plus className="w-4 h-4 mr-1" />
          노트 추가
        </Button>
      </div>
    </Card>
  );
}
